import type {
  ExtendedConstraints,
  EventHandlerCb,
  CapabilityConfig,
} from '../../types';
import {
  hasOnlyKeysFromSet,
  isStringArray,
  isValidContextConfig,
  isValidContext as isValidContextUtil,
} from '../../utils';
import {
  FronteggExtensionsFeatureKeys,
  FronteggExtensionsPermissionKeys,
} from '../../../entitlements/fronteggExtensionsKeys';

export const id = 'receive-context';

export enum Events {
  Update = 'receive-context:update',
  Other = 'receive-context:other',
}

export enum Actions {
  Request = 'receive-context:request',
}

export type UpdatePayload = AppContext;
export type OtherPayload = string;

// This section provides an overview of the key attributes used within the receive context capability.
// -   Role: Specifies whether the user is a Patient or a Provider.
// -   Username: The user's full name.
// -   Clinic ID: The unique identifier of the clinic hosting the call. This is visible only if the provider is associated with the clinic.
// -   Personal Room URL: The provider's personal room URL. This is visible to providers only.
// -   Locale: The language and regional settings chosen by the user.
// -   Theme: The visual theme applied to the extension, either "light" or "dark."
// -   Subscription Type: The provider's subscription type. This is visible to providers only.
// -   User ID: The unique identifier for the provider. This is visible to providers only.
// -   Session ID: This is the Vonage session ID, aka "channel" in our analytics. It is not the DB call ID.
// -   Ip: patient's IP address
// -   Avatar: The current user's profile picture URL.
// -   Features: Frontegg feature entitlements the provider has (e.g. premium-plan-feature, pro-plan-feature).
// -   Permissions: App-level roles from Frontegg (appUser, appAdmin, appOwner).
const VALID_CONTEXT_KEYS = [
  'role',
  'username',
  'avatar',
  'clinicId',
  'personalRoomUrl',
  'locale',
  'theme',
  'subscriptionType',
  'userId',
  'sessionId',
  'ip',
  'permissions',
] as const;

// Re-exported so SDK consumers can access these keys through Specs.ReceiveContext.
export { FronteggExtensionsFeatureKeys, FronteggExtensionsPermissionKeys };

export enum Role {
  Provider = 'Provider',
  Patient = 'Patient',
}
export type AppContext = {
  role?: Role;
  username?: string;
  avatar?: string;
  clinicId?: string;
  personalRoomUrl?: string;
  locale?: string;
  theme?: string;
  subscriptionType?: string;
  userId?: string;
  sessionId?: string;
  ip?: string;
  features?: FronteggExtensionsFeatureKeys[];
  permissions?: FronteggExtensionsPermissionKeys[];
};

type ContextKey = Exclude<keyof AppContext, 'features'>;

const VALID_CONFIG_KEYS = ['type', 'context', 'features'] as const;
export type Config = CapabilityConfig & {
  type: typeof id;
  context: ContextKey[];
  features?: FronteggExtensionsFeatureKeys[];
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  eventPayloadTuple:
    | [Events.Update, UpdatePayload]
    | [Events.Other, OtherPayload];
  eventHandlerTuple:
    | [Events.Update, EventHandlerCb<UpdatePayload>]
    | [Events.Other, EventHandlerCb<OtherPayload>];
  actionPayloadTuple: [Actions.Request];
}>;

export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  if (!isValidContextConfig(config, VALID_CONTEXT_KEYS)) {
    return false;
  }

  const { features } = config;
  if (features !== undefined && !isStringArray(features)) {
    return false;
  }

  return true;
}

const ARRAY_CONTEXT_KEYS: (keyof AppContext)[] = ['features', 'permissions'];

// `features` is no longer a valid CONFIG context key (extensions declare it
// via the top-level `features` config field), but it is still a valid PAYLOAD
// key the bridge delivers. The payload validator accepts both sets.
const VALID_PAYLOAD_KEYS = [...VALID_CONTEXT_KEYS, 'features'] as const;

export function isValidContext(context: unknown): context is AppContext {
  return isValidContextUtil<AppContext>(
    context,
    VALID_PAYLOAD_KEYS,
    [],
    ARRAY_CONTEXT_KEYS,
  );
}
